import React from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Form,
  FormInput,
  Button
} from "shards-react";
import PageTitle from "../../components/common/PageTitle";
import Select from "react-select";
import { strings } from "../../localization/OfflinePayments";
import { Link, Redirect } from "react-router-dom";
import { Language, API_URL } from "../../config";
import swal from "sweetalert2";
import { validateOfflinePaymentForm } from "../../utils/validations";

class ManageOfflinePayments extends React.Component {
  state = {
    organisations: [],
    organisationsData: [],
    plans: [],
    plansData: [],
    formData: {
      organisationId: null,
      planId: null,
      amount: "",
      startDate: "",
      endDate: "",
      paymentMethod: ""
    },
    submitting: false,
    redirectToList: false,
    isAmountDisabled: false,
    plansLoaded: false
  };

  constructor(props) {
    super(props);
    strings.setLanguage(Language);

    this.paymentMethods = [
      { value: "Cash", label: strings.cash },
      { value: "Bank Transfer", label: strings.bank_transfer },
    ];
  }

  componentDidMount() {
    this.getFilterData();
  }

  getFilterData = () => {
    // Fetch organisations
    fetch(`${API_URL}organisations`, {
      method: "GET",
      credentials: "include"
    })
      .then(res => res.json())
      .then(res => {
        if (res.result) {
          const orgs = res.result.map(item => ({
            value: item._id,
            label: item.data.name
          }));
          this.setState({
            organisations: orgs,
            organisationsData: res.result
          });
        }
      })
      .catch(err => {
        console.error("Error fetching organisations:", err);
      });

    // Fetch plans
    fetch(`${API_URL}plans`, {
      method: "GET",
      credentials: "include"
    })
      .then(res => res.json())
      .then(res => {
        if (res.result) {
          const plans = res.result.map(item => ({
            value: item._id,
            label: item.data.displayName
          }));
          this.setState({
            plans: plans,
            plansData: res.result,
            plansLoaded: true
          });
        }
      })
      .catch(err => {
        console.error("Error fetching plans:", err);
      });
  };

  handleFormChange = (field, value) => {
    var newState = {
      formData: {
        ...this.state.formData,
        [field]: value
      }
    };

    // Handle organisation selection with automatic plan selection
    if (field === "organisationId") {
      var selectedOrg = this.state.organisationsData.find(
        org => org._id === value.value
      );

      // Check if organisation has a planId and plans data is loaded
      if (selectedOrg && selectedOrg.data.planId && this.state.plansLoaded) {
        var selectedPlan = this.state.plans.find(
          plan => plan.value === selectedOrg.data.planId
        );
        var selectedPlanObj = this.state.plansData.find(
          plan => plan._id === selectedOrg.data.planId
        );

        // Auto-select the plan if found
        if (selectedPlan && selectedPlanObj) {
          // Check if plan is trial (planUnit === "lifetime")
          var isTrial = selectedPlanObj.data.planUnit === "lifetime";
          newState.formData.planId = selectedPlan;
          newState.formData.amount = isTrial ? "" : this.state.formData.amount;
          newState.isAmountDisabled = !!isTrial;
        }
      } else {
        // Reset plan selection and amount state if org has no planId
        newState.formData.planId = null;
        newState.formData.amount = "";
        newState.isAmountDisabled = false;
      }
    }

    if (field === "planId") {
      var selectedPlanObj = this.state.plansData.find(
        plan => plan._id === value.value
      );
      if (selectedPlanObj && selectedPlanObj.data.planUnit === "lifetime") {
        newState.formData.amount = "";
        newState.isAmountDisabled = true;
      } else {
        newState.isAmountDisabled = false;
      }
    }

    this.setState(newState);
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const { formData, isAmountDisabled } = this.state;

    // Validate form using utility
    const validation = validateOfflinePaymentForm(formData, strings, isAmountDisabled);

    if (!validation.isValid) {
      const firstError = validation.errors[0];
      swal.fire({
        icon: "error",
        title: firstError.type === "missing_fields" ? "Missing Required Fields" : "Invalid Date Range",
        text: firstError.message
      });
      return;
    }

    this.setState({ submitting: true });

    // Prepare request body - convert amount to number
    const requestBody = {
      amount: isAmountDisabled ? 0 : parseFloat(formData.amount),
      startDate: formData.startDate,
      endDate: formData.endDate,
      planId: formData.planId.value,
      paymentMode: "offline",
      payment_method: formData.paymentMethod.value,
      organisationId: formData.organisationId.value
    };

    fetch(`${API_URL}plans/offline-payments`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(requestBody)
    })
      .then(res => res.json())
      .then(res => {
        this.setState({ submitting: false });
        if (res._id) {
          swal.fire("Success", res.message || "Offline payment created successfully", "success")
            .then(() => {
              this.setState({ redirectToList: true });
            });
        } else {
          swal.fire("Error", res.message || "Failed to create payment", "error");
        }
      })
      .catch(err => {
        this.setState({ submitting: false });
        console.error("Error creating payment:", err);
        swal.fire("Error", "Failed to create payment. Please try again.", "error");
      });
  };

  handleCancel = () => {
    this.setState({ redirectToList: true });
  };

  render() {
    const { formData, submitting, redirectToList, isAmountDisabled } = this.state;

    if (redirectToList) {
      return <Redirect to="/offline-payments" />;
    }

    return (
      <Container fluid className="main-content-container px-4">
        <Row noGutters className="page-header py-4">
          <PageTitle
            sm="4"
            title={strings.add_offline_payment}
            subtitle={strings.overview}
            className="text-sm-left"
          />
        </Row>
        <Row>
          <Col lg="6">
            <Card small className="mb-4">
              <CardBody className="p-0 pb-3">
                <Form className="py-4" onSubmit={this.handleSubmit}>
                  <Row form className="mx-4">
                    <Col md="12" className="form-group">
                      <label htmlFor="organisationId">
                        {strings.select_org} *
                      </label>
                      <Select
                        id="organisationId"
                        value={formData.organisationId}
                        onChange={(value) =>
                          this.handleFormChange("organisationId", value)
                        }
                        options={this.state.organisations}
                        placeholder={strings.select_org}
                      />
                    </Col>
                  </Row>
                  <Row form className="mx-4">
                    <Col md="12" className="form-group">
                      <label htmlFor="planId">
                        {strings.select_plan_option} *
                      </label>
                      <Select
                        id="planId"
                        value={formData.planId}
                        onChange={(value) =>
                          this.handleFormChange("planId", value)
                        }
                        options={this.state.plans}
                        placeholder={strings.select_plan}
                      />
                    </Col>
                  </Row>
                  <Row form className="mx-4">
                    <Col md="12" className="form-group">
                      <label htmlFor="amount">{strings.amount} *</label>
                      <FormInput
                        id="amount"
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) =>
                          this.handleFormChange("amount", e.target.value)
                        }
                        placeholder={strings.enter_amount}
                        disabled={isAmountDisabled}
                      />
                    </Col>
                  </Row>
                  <Row form className="mx-4">
                    <Col md="12" className="form-group">
                      <label htmlFor="paymentMethod">
                        {strings.payment_method} *
                      </label>
                      <Select
                        id="paymentMethod"
                        value={formData.paymentMethod}
                        onChange={(value) =>
                          this.handleFormChange("paymentMethod", value)
                        }
                        options={this.paymentMethods}
                        placeholder={strings.payment_method}
                      />
                    </Col>
                  </Row>
                  <Row form className="mx-4">
                    <Col md="12" className="form-group">
                      <label htmlFor="startDate">{strings.start_date} *</label>
                      <FormInput
                        id="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={(e) =>
                          this.handleFormChange("startDate", e.target.value)
                        }
                      />
                    </Col>
                  </Row>
                  <Row form className="mx-4">
                    <Col md="12" className="form-group">
                      <label htmlFor="endDate">{strings.end_date} *</label>
                      <FormInput
                        id="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={(e) =>
                          this.handleFormChange("endDate", e.target.value)
                        }
                      />
                    </Col>
                  </Row>
                  <Row form className="mx-4">
                    <Col md="12" className="form-group">
                      <Button
                        type="submit"
                        theme="primary"
                        disabled={submitting}
                      >
                        {submitting ? "Saving..." : strings.save}
                      </Button>
                      <Button
                        type="button"
                        theme="secondary"
                        className="ml-2"
                        onClick={this.handleCancel}
                        disabled={submitting}
                      >
                        {strings.cancel}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
          <Col>
            <Link to="/offline-payments" className="float-left">
              <Button
                size="sm"
                id="go_back"
                theme="light"
                className="mb-2 mr-1"
                style={{
                  marginTop: "-84px",
                  position: "relative",
                  left: "-100px",
                }}
              >
                {strings.go_back}
              </Button>
            </Link>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default ManageOfflinePayments;
